import {
  mutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import React from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  DeactivateBudgetFailureToastMessage,
  DeactivateBudgetToastMessage,
} from 'src/app/Toast/Toast'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { connectedPush } from 'src/navigation/Lnk'
import { budgetOverviewUrl } from 'src/navigation/urls'
import { DeactivateCreditCardWriteResponse } from 'src/queries/creditCards/deactivateCreditCard/deactivateCreditCard.models'
import {
  deactivateCreditCard,
  recoverBackendErrorPayload,
} from 'src/queries/creditCards/deactivateCreditCard/deactivateCreditCard.network'
import { QueryError } from 'src/queries/network.utils'
import {
  CREDIT_CARDS_QK,
  creditCardsQueryKeys,
} from 'src/queries/queries.constants'
import { invalidateVirtualCardLists } from 'src/queries/virtualCards.queries'
import {
  DEACTIVATE_CREDIT_CARD_FAILURE,
  DEACTIVATE_CREDIT_CARD_INIT,
  DEACTIVATE_CREDIT_CARD_SUCCESS,
} from 'src/redux/actions/data/creditCards.actions'

export type DeactivateCreditCardVariables = {
  creditCardId: string
  creditCard: CreditCardListModel
}

export const deactivateCreditCardMutations = {
  deactivate: () =>
    mutationOptions<
      DeactivateCreditCardWriteResponse,
      QueryError,
      DeactivateCreditCardVariables
    >({
      mutationFn: (variables) => {
        // Fail fast rather than PUT /creditcards//deactivate, which the
        // backend would route as a list-level write. The call site guards
        // too, but the factory is the public boundary.
        if (!variables.creditCardId) {
          throw new Error(
            'deactivateCreditCardMutations.deactivate: creditCardId is required',
          )
        }
        return deactivateCreditCard(variables.creditCardId)
      },
    }),
}

/**
 * Deactivate a credit-card-backed budget (DeactivateBudget confirmation
 * flyout). Replaces the `deactivateCreditCardAndCloseModal` thunk. Preserves
 * its Redux lifecycle dispatches (INIT/SUCCESS/FAILURE — still consumed by
 * `deactivateBudget.reducer.ts`'s `deletingCreditCard` loading flag), the
 * success/failure toasts (built from the already-hydrated `creditCard`
 * variable passed in from the call site, not the response), and the
 * post-deactivate navigation to the budget overview page.
 *
 * Adds a NARROW invalidation of the card's own detail key plus every
 * list-shaped credit-card query (a list key has a filters object one slot
 * after the `creditcards` query-key segment, wherever an optional
 * `additionalKey` prefix — see `useQueryCreditCards` — puts that segment) —
 * never the family root `creditCardsQueryKeys.all`, which would also evict
 * every other card's unrelated detail/permissions cache. There is no
 * `DEACTIVATE_CREDIT_CARD_SUCCESS` case in `reactQueryInterop.reducer`, so
 * this is net-new, not a reproduction of an existing interop path.
 *
 * Deactivating a budget cascades on the backend to close its child virtual
 * cards, so list-shaped virtual-card queries are invalidated too — otherwise
 * they render the children as still-active until a natural refetch.
 */
export const useDeactivateCreditCard = () => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()

  return useMutation<
    DeactivateCreditCardWriteResponse,
    QueryError,
    DeactivateCreditCardVariables
  >({
    ...deactivateCreditCardMutations.deactivate(),
    onMutate: () => {
      dispatch({ type: DEACTIVATE_CREDIT_CARD_INIT })
    },
    onSuccess: (data, variables) => {
      // Toast + redirect stay on the `useMutation` callbacks, not the `mutate()`
      // call site `src/queries/AGENTS.md` prefers for them: the flyout unmounts
      // mid-flight if dismissed, and call-site callbacks are dropped on unmount —
      // taking the post-deactivate redirect with them.
      dispatch(connectedPush(budgetOverviewUrl(variables.creditCardId)))
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <DeactivateBudgetToastMessage budget={variables.creditCard} />
          ),
        }),
      )
      dispatch({ type: DEACTIVATE_CREDIT_CARD_SUCCESS, payload: data })
      queryClient.invalidateQueries({
        queryKey: creditCardsQueryKeys.detail(variables.creditCardId),
      })
      queryClient.invalidateQueries({
        predicate: (query) => {
          const qkIndex = query.queryKey.indexOf(CREDIT_CARDS_QK)
          return (
            qkIndex !== -1 &&
            typeof query.queryKey[qkIndex + 1] === 'object' &&
            query.queryKey[qkIndex + 1] !== null
          )
        },
      })
      // The backend cascade closes the child virtual cards, but the response
      // carries no child ids, so their detail keys can't be targeted; the
      // shared list-shaped invalidation is the narrowest key that covers the
      // cascade.
      invalidateVirtualCardLists(queryClient)
    },
    onError: (error, variables) => {
      dispatch(
        createSetIsToastOpen(true, {
          toastMessage: (
            <DeactivateBudgetFailureToastMessage
              budget={variables.creditCard}
            />
          ),
          failureColorScheme: true,
        }),
      )
      dispatch({
        type: DEACTIVATE_CREDIT_CARD_FAILURE,
        payload: recoverBackendErrorPayload(error.originalError ?? error),
      })
    },
  })
}
